// F1 (#3988) — sign-out data-safety guard: Angular-facing orchestration.
//
// Consumes the pure decision core in sign-out-guard.ts and runs the full
// pre-sign-out safety flow (fresh reachability probe -> classify -> bounded
// drain -> warning dialog). This half imports the AlertController /
// OnlineStateService / DatabaseWrapper graph, so — unlike sign-out-guard.ts —
// it is NOT node-loadable and carries no unit test of its own; the decision
// logic it wraps is covered by sign-out-guard.test.ts.

import type { OnlineStateService } from './online-state.service';
import type { DatabaseWrapper } from './database-wrapper';
import type { AlertController } from 'providers/alert-controller';
import type { LoggingService } from './logging.service';
import type { ImageUploadOutboxService } from './image-upload-outbox.service';
import { ConnectionState } from './online-state.service';
import { AlertButtonRole } from 'providers/alert-controller';
import { LL } from 'i18n/i18n.service';
import { classifySignOutSafety, SignOutSafety } from './sign-out-guard';
import { sleep } from '@cf-shared/shared-util';

/** Runs the pre-sign-out safety flow. Returns true when sign-out may proceed,
 * false when the user cancelled or the safety check could not complete. Never
 * throws: any error is reported and treated as "not safe to sign out" so the
 * caller can rely on the boolean alone. */
export async function confirmSignOutSafeAsync(deps: {
  onlineState: OnlineStateService;
  userDbWrapper: DatabaseWrapper | null;   // null when no user DB (never logged in) -> proceed
  alertCtrl: AlertController;
  logging: LoggingService;
  imageUploadOutbox: ImageUploadOutboxService;
}): Promise<boolean> {
  const { onlineState, userDbWrapper, alertCtrl, logging, imageUploadOutbox } = deps;
  if (!userDbWrapper) return true;
  try {
    // fresh probe, not the cached latch — mirrors couch-login-online-gate (#3878)
    const reachable = onlineState.connectionState === ConnectionState.Online
      || await onlineState.probeServerReachableAsync();
    // #3989: photos queued in the image upload outbox are unflushed local data
    // too — an ordinary sign-out discards them with everything else, so a
    // non-empty outbox must trigger the same drain/warn protection as unpushed
    // doc writes. (The wipe reaches that DB on every platform since #5269; see
    // image-upload-outbox.service.ts.)
    //
    // "Delete Private Data" is the one flow where a queued photo must NOT reach
    // this check, because the user has asked for it to be destroyed and the
    // question below would be untrue advice. That is handled at the source —
    // `purgeAccountAsync` empties the outbox — rather than by a special case
    // here. See design-docs/sync-integrity.md, "The change-tracking flags".
    const queuedImageCount = await imageUploadOutbox.pendingCountAsync();
    let decision = classifySignOutSafety({
      serverReachable: reachable,
      hasUnflushedChanges: userDbWrapper.hasUnflushedLocalChanges() || queuedImageCount > 0,
    });
    if (decision === SignOutSafety.DrainFirst) {
      // kick the image-outbox drain alongside the push drain; it no-ops when
      // the outbox is empty. both waits below start now and share the same
      // 10s wall-clock window, so the total wait stays bounded at ~10s.
      const outboxDrainDone = imageUploadOutbox.drainAsync();
      const boundedOutboxDrain = Promise.race([outboxDrainDone, sleep(10_000)]);
      // if the server was reachable but the push does not finish in 10s, fall
      // through to the warning dialog just like the offline case. Its message is
      // worded to cover both — "make sure you have a connection AND give it a
      // moment to finish uploading" — so the advice is right whether we never
      // reached the server or reached it but the upload was still in flight.
      const pushDrained = await userDbWrapper.whenPushDrainedAsync(10_000);
      // give the outbox drain its full window too: with queued images but no
      // pending doc push, the push wait returns immediately and would otherwise
      // warn before the just-started upload could finish
      await boundedOutboxDrain;
      // the push poll cannot see the outbox, so re-check it explicitly: still-
      // queued photos fall through to the same warning dialog as an undrained push
      const outboxDrained = (await imageUploadOutbox.pendingCountAsync()) === 0;
      decision = (pushDrained && outboxDrained) ? SignOutSafety.Proceed : SignOutSafety.WarnOffline;
    }
    if (decision === SignOutSafety.Proceed) return true;
    const choice = await alertCtrl.showAlertAsync<'signOut' | 'cancel'>({
      title: LL.profile.signOutOfflineWarning.title(),
      message: LL.profile.signOutOfflineWarning.message(),
      buttons: [
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: 'cancel' },
        { text: LL.profile.signOutOfflineWarning.signOutAnyway(), role: AlertButtonRole.Primary, result: 'signOut' },
      ],
    });
    return choice === 'signOut';
  } catch (err) {
    // if the safety check itself throws (e.g. a sessionStorage SecurityError in
    // a hardened browser, or the alert controller rejecting) we cannot prove the
    // local data is safe and may not have been able to warn the user. Surface it
    // and fail safe by BLOCKING the sign-out — never silently delete unsynced
    // data on an error path. The user loses nothing and can retry.
    logging.trackAbnormalException(err);
    return false;
  }
}
